import React from 'react';
import {
  Dashboard,
  Settings,
  PlaylistAddCheck,
  Storage,
  Widgets,
  Notifications,
  Group,
  PersonAdd,
  Speed,
  AccountBalance,
  Analytics,
  Handyman,
  Category,
  Security,
  MenuBook,
  Flag,
  ViewQuilt,
  RocketLaunchOutlined,
  Html,
  EmojiEvents,
  Checklist,
} from '@mui/icons-material';

import { RequiredRole } from '../../../routes/types';

export interface SidebarGroupConfig {
  /** Matches `sidebar.group` on tenant route metadata. */
  key: string;
  /** Display label of the group (or of the single item, if the group has one route). */
  label: string;
  /** Icon component (from @mui/icons-material). */
  icon: React.ComponentType<{ sx?: any }>;
  /** Sort order among top-level sidebar entries. */
  order: number;
  /** Hide the entire group unless the current user has this role. Flag-off gate only. */
  requiredRole?: RequiredRole;
  /**
   * RBAC_V2 only: hide this group entirely under the flag, regardless of
   * `SECTION_GATES` (e.g. `analytics`, replaced by `insights`). No effect
   * when the flag is off.
   */
  hiddenWhenV2?: boolean;
  /**
   * RBAC_V2 only: this group exists ONLY under the flag (e.g. `insights`) —
   * hidden unconditionally when the flag is off, even though it has no
   * `requiredRole` for the legacy path.
   */
  onlyWhenV2?: boolean;
  /**
   * This group IS the V1 Actions surface — hidden entirely when the tenant has
   * the `hideV1Actions` attribute on.
   */
  isV1Actions?: boolean;
  /**
   * Label to use INSTEAD of `label` when `hideV1Actions` is on. Task V2 becomes
   * simply "Actions" once it is the only actions surface the tenant has.
   */
  labelWhenV1ActionsHidden?: string;
}

/**
 * Optional non-route entries — external URLs shown as leaves in the
 * sidebar. They're declared here rather than as routes because they open
 * in a new tab and don't participate in in-app routing.
 */
export interface SidebarExternalItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ sx?: any }>;
  order: number;
  /** Fixed destination. Mutually exclusive with `externalUrlForEnv`. */
  externalUrl?: string;
  /**
   * Destination derived from the environment picked in the top-bar dropdown.
   * Used where the external tool is deployed per environment and following the
   * link into the wrong one is worse than not having the link.
   */
  externalUrlForEnv?: (env: string) => string;
  requiredRole?: RequiredRole;
}

/**
 * flex-app-builder's host for an environment.
 *
 *   dev01 -> https://appbuilder.dev.sunnybenefits.com
 *   qa    -> https://appbuilder.qa.sunnybenefits.com
 *   uat   -> https://appbuilder.uat.sunnybenefits.com
 *   integ -> https://appbuilder.integ.sunnybenefits.com
 *   prod  -> https://appbuilder.sunnybenefits.com
 *
 * Two irregularities are why this is a function and not a lookup table: prod
 * carries no subdomain at all, and the authoring env is named `dev01` here but
 * `dev` in the hostname. Everything else follows `appbuilder.<env>`, so an
 * environment added later (dev02, say) resolves without touching this file.
 */
export const appBuilderUrlForEnv = (env: string): string => {
  if (env === 'prod') return 'https://appbuilder.sunnybenefits.com';
  const host = env === 'dev01' ? 'dev' : env;
  return `https://appbuilder.${host}.sunnybenefits.com`;
};

/**
 * All sidebar groups, ordered top-to-bottom as they appear in the nav.
 * A group with a single route renders as a top-level leaf item; a group
 * with multiple routes renders as an expandable section.
 *
 * NOTE: group keys here must match `sidebar.group` on routes declared in
 * `src/routes/domains/*.tsx`. If you add a group here, populate a route's
 * metadata too; if you remove a group, make sure no route still references
 * it. Mismatches are caught at dev-time by `useSidebarNav`.
 */
export const SIDEBAR_GROUPS: SidebarGroupConfig[] = [
  { key: 'dashboard', label: 'Dashboard', icon: Dashboard, order: 1 },
  { key: 'tenant', label: 'Tenant', icon: Settings, order: 2 },
  // R7: `field-definitions`, `scripts`, and the RBAC/secret-defaults items
  // inside `administration` moved to /admin/*. The tenant-side groups are
  // removed here so the items don't double-appear in nav.
  { key: 'actions', label: 'Actions', icon: PlaylistAddCheck, order: 4, isV1Actions: true },
  // Task V2 — additive capability role; the whole section (metrics, completion
  // criteria, Library V2, All Actions V2) is visible only to super-admins and
  // users holding `taskv2`. Mirrors the `flex` capability-role group.
  {
    key: 'task-v2', label: 'Task V2', icon: Checklist, order: 4.5, requiredRole: 'taskv2',
    // On a tenant with `hideV1Actions`, this is the only actions surface there
    // is, so it takes the plain name.
    labelWhenV1ActionsHidden: 'Actions',
  },
  { key: 'data-integrations', label: 'Data Integrations', icon: Storage, order: 5, requiredRole: 'admin' },
  // CPT-1375 AC7: renamed from 'CMS' to 'Content (Old)' — the name the story
  // specifies, so it reads as the predecessor of the 'Content' tab beside it.
  // Key unchanged: it is the route metadata contract, not a label.
  { key: 'cms', label: 'Content (Old)', icon: Widgets, order: 6 },
  { key: 'pdf-to-html', label: 'PDF to HTML', icon: Html, order: 6.4 },
  { key: 'storyblok-content', label: 'Content', icon: MenuBook, order: 6.5 },
  { key: 'flex-components', label: 'Flex Components', icon: ViewQuilt, order: 6.6, requiredRole: 'flex' },
  { key: 'notifications', label: 'Notifications', icon: Notifications, order: 7, requiredRole: 'admin' },
  { key: 'consumer-360', label: 'Consumer 360', icon: Group, order: 9 },
  { key: 'member-import', label: 'Member Import', icon: PersonAdd, order: 10 },
  { key: 'operations', label: 'Operations', icon: Speed, order: 11, requiredRole: 'admin' },
  { key: 'financial', label: 'Financial', icon: AccountBalance, order: 12, requiredRole: 'admin' },
  // RBAC_V2: replaced by `insights` (same slot, order 13) — see R12. Flag
  // off ⇒ Analytics renders exactly as today; flag on ⇒ hidden.
  { key: 'analytics', label: 'Analytics', icon: Analytics, order: 13, requiredRole: 'admin', hiddenWhenV2: true },
  // RBAC_V2 only — takes the Analytics slot so nav position is stable. The
  // tenant route (paths.insights + observability.tsx) lands in a separate
  // work package; until it registers a route with `sidebar.group:
  // 'insights'`, this entry has no children and renders nothing.
  { key: 'insights', label: 'Insights', icon: Analytics, order: 13, onlyWhenV2: true },
  { key: 'milestones', label: 'Milestones', icon: EmojiEvents, order: 8 },
  { key: 'program-year', label: 'Config', icon: Category, order: 15 },
  // `administration` still holds API Keys / Environment Promotion / Bulk
  // Operations / File Validation. RBAC and Secret Defaults Audit were moved
  // to /admin; the remaining items keep this group relevant at the tenant
  // level.
  { key: 'administration', label: 'Administration', icon: Security, order: 16, requiredRole: 'admin' },
  // Promote — additive capability role; visible to super-admins and users
  // holding `promoter` (existing `workflow_creator` API access is unaffected).
  { key: 'environment-promotion', label: 'Promote', icon: RocketLaunchOutlined, order: 16.5, requiredRole: 'promoter' },
  // Feature Flags — super-admin only; shows Mixpanel flag state per tenant.
  { key: 'feature-flags', label: 'Feature Flags', icon: Flag, order: 17, requiredRole: 'admin' },
];

export const SIDEBAR_EXTERNAL_ITEMS: SidebarExternalItem[] = [
  {
    key: 'build-in-flex',
    label: 'Build in Flex',
    icon: Handyman,
    order: 14,
    // Follows the environment dropdown: opening the dev app builder while the
    // portal is pointed at prod is the kind of mistake that is only obvious
    // afterwards.
    externalUrlForEnv: appBuilderUrlForEnv,
    requiredRole: 'admin',
  },
];
